<template>
  <div class="gallery-page">
    <div class="gallery-header">
      <div class="header-content">
        <h1 class="page-title">Galeri DELIMaFLiX</h1>
        <p class="page-subtitle">Koleksi lengkap gambar dan media visual dari platform DELIMaFLiX</p>
      </div>
    </div>

    <div class="filter-section">
      <div class="filter-container">
        <div class="search-box">
          <input v-model="searchQuery" type="text" placeholder="Cari gambar..." class="search-input" />
          <img src="../assets/icons/search-normal.svg" class="search-icon" alt="Search" />
        </div>

        <div class="filter-dropdown">
          <select v-model="selectedCategory" class="category-select">
            <option value="">Semua Kategori</option>
            <option value="educational">Pendidikan</option>
            <option value="activity">Aktiviti</option>
            <option value="event">Acara</option>
            <option value="resource">Sumber</option>
            <option value="official">Rasmi</option>
          </select>
        </div>
      </div>
    </div>

    <div class="gallery-container">
      <div class="gallery-grid">
        <div v-for="image in images" :key="image.id" class="gallery-item" @click="openModal(image)">
          <div class="image-container">
            <img :src="image.src" :alt="image.title" class="gallery-image" loading="lazy" />
            <div class="image-overlay">
              <div class="overlay-content">
                <h3 class="image-title">{{ image.title }}</h3>
                <p class="image-description">{{ image.description }}</p>
                <div class="image-meta">
                  <span class="image-category">{{ image.category }}</span>
                  <span class="image-date">{{ formatDate(image.date) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Memuat gambar...</p>
      </div>

      <div v-if="!isLoading && images.length === 0" class="empty-state">
        <div class="empty-icon">📷</div>
        <h3>Tiada gambar ditemui</h3>
        <p>Cuba ubah carian atau kategori anda</p>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button @click="closeModal" class="modal-close">&times;</button>
        <div class="modal-image-container">
          <img :src="selectedImage.src" :alt="selectedImage.title" class="modal-image" loading="lazy" />
        </div>
        <div class="modal-info">
          <h2>{{ selectedImage.title }}</h2>
          <p>{{ selectedImage.description }}</p>
          <div class="modal-meta">
            <span class="meta-item"><strong>Kategori:</strong> {{ selectedImage.category }}</span>
            <span class="meta-item"><strong>Tarikh:</strong> {{ formatDate(selectedImage.date) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';

export default {
  name: 'GalleryPage',
  setup() {
    const images = ref([]);
    const searchQuery = ref('');
    const selectedCategory = ref('');
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedImage = ref({});

    const loadImagesFromBackend = async () => {
      isLoading.value = true;
      try {
        const response = await axios.get('/api/v1/gallery/list', {
          params: {
            search: searchQuery.value,
            category: selectedCategory.value,
          },
        });
        images.value = response.data.data.map(item => ({
          id: item.id,
          src: item.image_url,
          title: item.title,
          description: item.description,
          category: item.category,
          date: item.date_taken,
        }));
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const openModal = (image) => {
      selectedImage.value = image;
      showModal.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
      showModal.value = false;
      selectedImage.value = {};
      document.body.style.overflow = 'auto';
    };

    const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('ms-MY', options);
    };

    watch([searchQuery, selectedCategory], () => {
      loadImagesFromBackend();
    });

    onMounted(() => {
      loadImagesFromBackend();
    });

    return {
      images,
      searchQuery,
      selectedCategory,
      isLoading,
      showModal,
      selectedImage,
      openModal,
      closeModal,
      formatDate,
    };
  },
};
</script>

<style scoped>
.gallery-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 0;
}

.gallery-header {
  text-align: center;
  margin-bottom: 3rem;
  padding: 0 1rem;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #718096;
  line-height: 1.6;
}

.filter-section {
  max-width: 1200px;
  margin: 0 auto 2rem;
  padding: 0 1rem;
}

.filter-container {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  min-width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  opacity: 0.5;
}

.category-select {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.category-select:focus {
  outline: none;
  border-color: #667eea;
}

.gallery-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.gallery-item {
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.gallery-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.image-container {
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.gallery-image {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item:hover .gallery-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.gallery-item:hover .image-overlay {
  transform: translateY(0);
}

.overlay-content {
  text-align: left;
}

.image-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.image-description {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.image-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  opacity: 0.8;
}

.image-category {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  text-transform: capitalize;
}

.loading-container {
  text-align: center;
  padding: 3rem;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #718096;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.pagination-btn, .page-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled), .page-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 1rem;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
  transition: background 0.3s ease;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.7);
}

.modal-image-container {
  position: relative;
}

.modal-image {
  width: 100%;
  height: auto;
  max-height: 60vh;
  object-fit: contain;
}

.modal-info {
  padding: 2rem;
}

.modal-info h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2d3748;
}

.modal-info p {
  color: #718096;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.modal-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.meta-item {
  color: #4a5568;
}

.meta-item strong {
  color: #2d3748;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }

  .filter-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }

  .pagination {
    flex-wrap: wrap;
  }

  .modal-content {
    margin: 1rem;
    max-width: calc(100% - 2rem);
  }

  .modal-info {
    padding: 1rem;
  }
}
</style>
